import { FC, useEffect, useMemo, useState } from 'react';
import { FloatingLabel, Form, ListGroup } from 'react-bootstrap';
import { Hr } from '../Hr';
import { HTHBuildingModal, HTHBuildingsFilter } from '.';
import { hthHelper } from 'src/services';
import { typeBlock, typeConfigDoc, typeHTHBuilding, typeHTHTerritory, typeHTHFace } from '@monorepo/models';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    block: typeBlock | null;
    congregationConfig: typeConfigDoc;
    hthTerritory: typeHTHTerritory;
};

export const HTHAllBuildings: FC<propsType> = ({ block, congregationConfig, hthTerritory }) => {
    const [buildingAddress, setBuildingAddress] = useState('');
    const [currentBuilding, setCurrentBuilding] = useState<typeHTHBuilding | null>(null);
    const [currentFace, setCurrentFace] = useState<typeHTHFace | null>(null);
    const [filter, setFilter] = useState([...new Set(hthTerritory.faces.map(p => p.block))]);
    const [selectedAddress, setSelectedAddress] = useState('');
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    const allBuildings: (typeHTHBuilding & { block: typeBlock; face: typeHTHFace; })[] = useMemo(() =>
        hthTerritory.faces
            .map(p => p.buildings ? p.buildings.map(b => ({ ...b, face: p, block: p.block})) : [])
            .filter(p => p.length)
            .flat()
            .filter(p => filter.includes(p.block))
            .sort((a, b) => a.face.street.localeCompare(b.face.street) || (a.streetNumber - b.streetNumber))
    , [filter, hthTerritory.faces]);

    const closeBuildingModalHandler = () => {
        setCurrentBuilding(null);
        setCurrentFace(null);
        setSelectedAddress("");
    }

    useEffect(() => {
        setCurrentFace(x => x ?
            hthTerritory.faces.find(p => p.id === x.id) ?? null
            :
            x
        );
    }, [hthTerritory]);

    useEffect(() => {
        setCurrentBuilding(x => x ?
            currentFace?.buildings?.find(b => b.streetNumber === x.streetNumber) ?? null
            :
            x
        );
    }, [currentFace]);

    return (
        <>
        {currentFace && currentBuilding &&
            <HTHBuildingModal
                closeBuildingModalHandler={closeBuildingModalHandler}
                config={congregationConfig}
                currentBuilding={currentBuilding}
                currentFace={currentFace}
                territoryNumber={hthTerritory.territoryNumber}
            />
        }
        {!block &&
            <HTHBuildingsFilter
                filter={filter}
                hthTerritory={hthTerritory}
                setFilter={setFilter}
                usingLettersForBlocks={!!congregationConfig?.isUsingLettersForBlocks}
            />
        }
        <Hr classes={'text-center my-5 mx-auto'} styles={{ maxWidth: isMobile ? '95%' : '85%' }} />
        {!!allBuildings?.length ?
            <>
            <div className={'container maxw-400'}>
                <h1 className={`text-center mb-5 ${isDarkMode ? 'text-white' : ''}`}>
                    BUSCAR EDIFICIO<span className={'h5 fw-normal ms-1'}>({allBuildings.length})</span>
                </h1>
                <FloatingLabel
                    className={'mb-3 text-dark'}
                    label={"Dirección"}
                >
                    <Form.Control
                        type={'text'}
                        className={'form-control'}
                        placeholder={""}
                        value={buildingAddress}
                        onChange={e => setBuildingAddress((e.target as HTMLInputElement).value)}
                        // autoFocus
                    />
                </FloatingLabel>
                <ListGroup as={'ul'}>
                    {allBuildings
                     .filter(b =>
                        `${b.face.street} ${b.streetNumber}`.toLowerCase().includes(buildingAddress.toLowerCase())
                        || `${b.face.street} ${b.streetNumber2}`.toLowerCase().includes(buildingAddress.toLowerCase())
                        || `${b.face.street} ${b.streetNumber3}`.toLowerCase().includes(buildingAddress.toLowerCase()))
                     .map(b =>
                        <ListGroup.Item key={b.face.street + "-" + b.streetNumber}
                            as={'li'}
                            className={`pointer ${isDarkMode ? 'bg-lightdark text-white' : 'bg-light bg-gradient' } hover-primary`}
                            active={selectedAddress === `${b.face.street} ${b.streetNumber}`}
                            onClick={() => {
                                setSelectedAddress(`${b.face.street} ${b.streetNumber}`);
                                setCurrentFace(b.face);
                                setCurrentBuilding(b);
                            }}
                        >
                            <div className='row py-2'>
                                <h5 className='text-center'>
                                    {b.face.street} {b.streetNumber}{b.streetNumber2 ? `/${b.streetNumber2}` : ''}{b.streetNumber3 ? `/${b.streetNumber3}` : ''}
                                </h5>
                            </div>
                            <div className='row text-center'>
                                <span className='text-center'>
                                    {hthHelper.GetFreeHouseholds(b)} libres | Últ. vez: {hthHelper.GetLastTimeOfBuilding(b)}
                                </span>
                            </div>
                        </ListGroup.Item>
                    )}
                </ListGroup>
            </div>
            </>
            :
            <h4 className={`text-center mt-4 ${isDarkMode ? 'text-white' : ''}`}>
                {!!filter.length ? 'No hay Edificios cargados en este Territorio' : 'Habilitar alguna manzana'}
            </h4>
        }
        </>
    );
}
